import React, { useState, useEffect, useCallback } from 'react';
import { Editor } from 'react-draft-wysiwyg';
import { EditorState, ContentState, convertToRaw, convertFromHTML } from 'draft-js';
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css';
import './EditorDraft.css';

/**
 * Callback para subida de imágenes (convierte a base64)
 */
const uploadImageCallback = (file) => {
  console.log('📷 Subiendo imagen:', file.name, 'Tamaño:', file.size);
  
  return new Promise((resolve, reject) => {
    if (!file) {
      console.error('❌ No se proporcionó archivo');
      reject(new Error('No file provided'));
      return;
    }

    if (!file.type.startsWith('image/')) {
      console.error('❌ El archivo no es una imagen:', file.type);
      reject(new Error('File is not an image'));
      return;
    }

    const reader = new FileReader();
    
    reader.onload = (e) => {
      const result = e.target.result;
      console.log('✅ Imagen cargada exitosamente, tamaño base64:', result.length);
      resolve({ data: { link: result } });
    };
    
    reader.onerror = (error) => {
      console.error('❌ Error al leer archivo:', error);
      reject(error);
    };
    
    reader.readAsDataURL(file);
  });
};

/**
 * EditorDraft - Editor de texto rico basado en react-draft-wysiwyg
 * Reemplaza a Quill/react-quill
 * 
 * Features:
 * - Dirección LTR garantizada
 * - Soporte de imágenes
 * - Toolbar completo
 * - Conversión HTML automática
 */
const EditorDraft = ({
  value = '',
  handleChange,
  placeholder = 'Escriba aquí...',
  fixedHeight = '300px',
  toolbarOptions: toolbarOptionsOverride,
}) => {
  const [editorState, setEditorState] = useState(() => EditorState.createEmpty());
  const [isInitialized, setIsInitialized] = useState(false);

  // Función para convertir bloques de Draft.js a HTML
  const convertToHTML = useCallback((rawContent) => {
    if (!rawContent || !rawContent.blocks) return '';
    
    let html = '';
    
    rawContent.blocks.forEach(block => {
      const text = block.text;
      let blockHtml = text;
      
      // Aplicar estilos inline
      if (block.inlineStyleRanges && block.inlineStyleRanges.length > 0) {
        // Ordenar rangos por offset descendente para no alterar posiciones
        const sortedRanges = [...block.inlineStyleRanges].sort((a, b) => b.offset - a.offset);
        
        sortedRanges.forEach(range => {
          const start = range.offset;
          const end = range.offset + range.length;
          const styledText = blockHtml.substring(start, end);
          
          let wrappedText = styledText;
          switch (range.style) {
            case 'BOLD':
              wrappedText = `<strong>${styledText}</strong>`;
              break;
            case 'ITALIC':
              wrappedText = `<em>${styledText}</em>`;
              break;
            case 'UNDERLINE':
              wrappedText = `<u>${styledText}</u>`;
              break;
            case 'STRIKETHROUGH':
              wrappedText = `<s>${styledText}</s>`;
              break;
            default:
              break;
          }
          
          blockHtml = blockHtml.substring(0, start) + wrappedText + blockHtml.substring(end);
        });
      }
      
      // Aplicar tipo de bloque
      switch (block.type) {
        case 'header-one':
          html += `<h1>${blockHtml}</h1>`;
          break;
        case 'header-two':
          html += `<h2>${blockHtml}</h2>`;
          break;
        case 'header-three':
          html += `<h3>${blockHtml}</h3>`;
          break;
        case 'header-four':
          html += `<h4>${blockHtml}</h4>`;
          break;
        case 'header-five':
          html += `<h5>${blockHtml}</h5>`;
          break;
        case 'header-six':
          html += `<h6>${blockHtml}</h6>`;
          break;
        case 'unordered-list-item':
          html += `<li>${blockHtml}</li>`;
          break;
        case 'ordered-list-item':
          html += `<li>${blockHtml}</li>`;
          break;
        case 'blockquote':
          html += `<blockquote>${blockHtml}</blockquote>`;
          break;
        case 'atomic':
          // Manejar entidades como imágenes
          if (block.entityRanges && block.entityRanges.length > 0) {
            const entityKey = block.entityRanges[0].key;
            const entity = rawContent.entityMap[entityKey];
            if (entity && entity.type === 'IMAGE') {
              html += `<img src="${entity.data.src}" alt="${entity.data.alt || ''}" style="max-width: 400px; max-height: 300px; width: auto; height: auto; object-fit: contain;" />`;
            }
          }
          break;
        default:
          html += blockHtml ? `<p>${blockHtml}</p>` : '<p><br/></p>';
      }
    });
    
    // Envolver listas
    html = html.replace(/(<li>.*?<\/li>)+/g, (match) => {
      return `<ul>${match}</ul>`;
    });
    
    return html;
  }, []);

  // Inicializar el editor con el valor HTML inicial
  useEffect(() => {
    if (value && !isInitialized) {
      try {
        const blocksFromHTML = convertFromHTML(value);
        if (blocksFromHTML.contentBlocks && blocksFromHTML.contentBlocks.length > 0) {
          const contentState = ContentState.createFromBlockArray(
            blocksFromHTML.contentBlocks,
            blocksFromHTML.entityMap
          );
          setEditorState(EditorState.createWithContent(contentState));
        }
        setIsInitialized(true);
      } catch (error) {
        console.warn('Error al parsear HTML inicial:', error);
        setIsInitialized(true);
      }
    }
  }, [value, isInitialized]);

  // Manejar cambios en el editor
  const onEditorStateChange = (newEditorState) => {
    setEditorState(newEditorState);
    
    // Convertir a HTML y enviar al padre
    const contentState = newEditorState.getCurrentContent();
    const rawContent = convertToRaw(contentState);
    const html = convertToHTML(rawContent);
    
    // Solo enviar si hay contenido real
    const hasText = rawContent.blocks.some(block => block.text.trim() !== '');
    if (hasText) {
      handleChange(html);
    } else {
      handleChange('');
    }
  };

  // Configuración del toolbar. `toolbarOptionsOverride` es opcional (ver
  // InputEditorHtml/NuevaConvocatoriaMasivo.jsx "Lineamientos"): permite ocultar
  // botones SOLO en el caller que lo pase, sin tocar el toolbar completo por
  // default que siguen usando el resto de los usos de este editor.
  const toolbarOptionsDefault = {
    options: ['inline', 'blockType', 'list', 'link', 'image', 'history'],
    inline: {
      options: ['bold', 'italic', 'underline'],
    },
    blockType: {
      options: ['Normal', 'H1', 'H2', 'H3'],
    },
    list: {
      options: ['unordered', 'ordered'],
    },
    link: {
      showOpenOptionOnHover: true,
      defaultTargetOption: '_blank',
      options: ['link', 'unlink'],
    },
    image: {
      uploadEnabled: true,
      uploadCallback: uploadImageCallback,
      previewImage: true,
      inputAccept: 'image/gif,image/jpeg,image/jpg,image/png,image/svg',
      alt: { present: false, mandatory: false },
      defaultSize: {
        height: '300px',
        width: '400px',
      },
      alignmentEnabled: false,
    },
  };

  const toolbarOptions = toolbarOptionsOverride ?? toolbarOptionsDefault;

  return (
    <div 
      className="editor-draft-container" 
      dir="ltr"
      style={{ 
        direction: 'ltr',
        textAlign: 'left'
      }}
    >
      <Editor
        editorState={editorState}
        onEditorStateChange={onEditorStateChange}
        toolbar={toolbarOptions}
        placeholder={placeholder}
        localization={{
          locale: 'es',
        }}
        editorStyle={{
          minHeight: fixedHeight,
          padding: '10px',
          border: '1px solid #ddd',
          borderTop: 'none',
          direction: 'ltr',
          textAlign: 'left',
        }}
        toolbarStyle={{
          border: '1px solid #ddd',
          borderBottom: 'none',
          direction: 'ltr',
        }}
      />
    </div>
  );
};

export default EditorDraft;
